// src/pages/OrgProfile.tsx
import { useEffect, useState } from "react";
import api from "../lib/axios";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Eye, EyeOff, ClipboardPaste, X } from "lucide-react";

/* ──────────────────────────────
   Minimal Modal (no deps)
   ────────────────────────────── */
function LiteModal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-auto p-4 text-sm text-gray-700">{children}</div>
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────
   Reuse the same Privacy Policy body
   ────────────────────────────── */
function PrivacyPolicyBody() {
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">Effective Date: {new Date().toLocaleDateString()}</p>
      <p>
        At <strong>InfuseED</strong>, we take privacy seriously. This policy explains what we collect,
        why we collect it, and what we do (and don’t do) with it.
      </p>
      <h4 className="font-semibold">1. Information We Collect</h4>
      <ul className="list-disc ml-5 space-y-1">
        <li><strong>Organization info</strong> you provide (school name, Canvas base URL, contact details).</li>
        <li><strong>Authentication tokens</strong> you provide to connect to Canvas.</li>
        <li><strong>Usage data</strong> like timestamps and status codes for debugging and security.</li>
      </ul>
      <p>We do not collect student grades or personally identifiable student data unless a feature explicitly requires it and you enable it.</p>

      <h4 className="font-semibold">2. How We Use Data</h4>
      <ul className="list-disc ml-5 space-y-1">
        <li>To connect InfuseED to your Canvas instance and provide requested features.</li>
        <li>To troubleshoot, secure, and improve the service.</li>
      </ul>
      <p>We do not sell your data or use it for marketing without your permission.</p>

      <h4 className="font-semibold">3. Security</h4>
      <ul className="list-disc ml-5 space-y-1">
        <li>Tokens are encrypted in transit and at rest (TLS 1.2+, AES-256).</li>
        <li>Access to tokens is restricted; we follow least-privilege principles.</li>
        <li>Only backend services access tokens; staff cannot view tokens in plain text.</li>
      </ul>

      <h4 className="font-semibold">4. Sharing</h4>
      <p>We don’t share data except when required by law or to operate the service (e.g., calling Canvas APIs).</p>

      <h4 className="font-semibold">5. Your Choices</h4>
      <ul className="list-disc ml-5 space-y-1">
        <li>Update org details any time in <em>Organization Profile</em>.</li>
        <li>Revoke your token in Canvas to immediately cut off access.</li>
        <li>Request deletion: <a className="text-blue-600 underline" href="mailto:support@getinfused.net">support@getinfused.net</a>.</li>
      </ul>

      <h4 className="font-semibold">6. Changes</h4>
      <p>We’ll update this page when the policy changes and notify you of material updates.</p>

      <h4 className="font-semibold">7. Contact</h4>
      <p>Questions? <a className="text-blue-600 underline" href="mailto:support@getinfused.net">support@getinfused.net</a></p>
    </div>
  );
}

type Org = {
  org_name: string;
  canvas_base_url: string;
  contact_name: string;
  contact_phone?: string | null;
};

type CanvasTestResult = {
  ok: boolean;
  user?: { id?: number; name?: string; login_id?: string };
  admin_like_access?: boolean;
};

export default function OrgProfile() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  // data
  const [org, setOrg] = useState<Org>({
    org_name: "",
    canvas_base_url: "",
    contact_name: "",
    contact_phone: "",
  });

  // edit mode
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Org>(org);
  const [saving, setSaving] = useState(false);

  // canvas token update UI
  const [showToken, setShowToken] = useState(false);
  const [showTokenText, setShowTokenText] = useState(false);
  const [token, setToken] = useState("");
  const [tokenSaving, setTokenSaving] = useState(false);
  const [tokenOk, setTokenOk] = useState("");
  const [tokenErr, setTokenErr] = useState("");

  // validation/test
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<CanvasTestResult | null>(null);

  // privacy modal
  const [policyOpen, setPolicyOpen] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/org/profile");
      const d = res.data || {};
      const next: Org = {
        org_name: d.org_name ?? "",
        canvas_base_url: d.canvas_base_url ?? "",
        contact_name: d.contact_name ?? "",
        contact_phone: d.contact_phone ?? "",
      };
      setOrg(next);
      setDraft(next);
    } catch (e) {
      console.error(e);
      setErr("Could not load organization profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = () => {
    setDraft(org);
    setEditing(true);
    setOk("");
    setErr("");
  };

  const cancelEdit = () => {
    setDraft(org);
    setEditing(false);
  };

  const save = async () => {
    setErr("");
    setOk("");
    if (!draft.org_name || !draft.canvas_base_url || !draft.contact_name) {
      setErr("Please fill out all required fields.");
      return;
    }
    try {
      setSaving(true);
      await api.put("/org/profile", {
        org_name: draft.org_name.trim(),
        canvas_base_url: draft.canvas_base_url.trim(),
        contact_name: draft.contact_name.trim(),
        contact_phone: (draft.contact_phone || "").trim() || null,
      });
      setOrg(draft);
      setOk("Saved!");
      setEditing(false);
    } catch (e: any) {
      console.error(e);
      setErr(e?.response?.data?.detail || "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setToken(text.trim());
        setTestResult(null);
        setTokenErr("");
        setTokenOk("");
      }
    } catch {
      setTokenErr("Couldn’t read from clipboard. Paste manually (Ctrl/Cmd+V).");
    }
  };

  const testConnection = async () => {
    setTokenErr("");
    setTokenOk("");
    setTestResult(null);

    const baseUrl = (org.canvas_base_url || "").trim();
    if (!baseUrl) {
      setTokenErr("Set your Canvas base URL first in the Organization Profile above.");
      return;
    }
    

    const t = token.trim();
    if (t.length < 20) {
      setTokenErr("That looks too short. Paste your Canvas Admin Personal Access Token.");
      return;
    }

    try {
      setTesting(true);
      const res = await api.post("/onboarding/canvas/test", {
        base_url: baseUrl,
        token: t,
      });
      const data = res.data as CanvasTestResult;
      setTestResult(data);
      setTokenOk(
        data?.admin_like_access
          ? "Connection looks good (admin endpoints detected)."
          : "Token is valid. Note: limited permissions for admin-only endpoints."
      );
    } catch (e: any) {
      console.error("Canvas test failed", e);
      setTestResult(null);
      setTokenErr(e?.response?.data?.detail || "Connection test failed.");
    } finally {
      setTesting(false);
    }
  };

  const saveToken = async () => {
    setTokenErr("");
    setTokenOk("");
    const t = token.trim();
    if (t.length < 20) {
      setTokenErr("That looks too short. Paste your Canvas Admin Personal Access Token.");
      return;
    }
    if (!testResult?.user) {
      setTokenErr("Please test your connection before saving.");
      return;
    }
    try {
      setTokenSaving(true);
      await api.post("/onboarding/canvas/token", { token: t });
      setTokenOk("Canvas token updated.");
      setToken("");
      setTestResult(null);
    } catch (e: any) {
      console.error(e);
      setTokenErr(e?.response?.data?.detail || "Could not save token.");
    } finally {
      setTokenSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Organization Profile</h1>
        {!editing ? (
          <button
            onClick={startEdit}
            className="text-sm underline text-gray-600 hover:text-black"
          >
            Edit
          </button>
        ) : null}
      </div>

      {ok && <div className="bg-green-50 text-green-700 text-sm p-2 rounded mb-3">{ok}</div>}
      {err && <div className="bg-red-50 text-red-700 text-sm p-2 rounded mb-3">{err}</div>}

      {!editing ? (
        // ===== Read view =====
        <div className="space-y-4">
          <Field label="Organization name" value={org.org_name} />
          <Field label="Canvas base URL" value={org.canvas_base_url} />
          <Field label="Contact name" value={org.contact_name} />
          <Field label= "Contact phone" value={org.contact_phone || "—"} />
        </div>
      ) : (
        // ===== Edit view =====
        <div className="space-y-3">
          <Input
            placeholder="Organization name"
            value={draft.org_name}
            onChange={(e) => setDraft({ ...draft, org_name: e.target.value })}
          />
          <Input
            placeholder="Canvas base URL (https://...instructure.com)"
            value={draft.canvas_base_url}
            onChange={(e) => setDraft({ ...draft, canvas_base_url: e.target.value })}
          />
          <Input
            placeholder="Contact name"
            value={draft.contact_name}
            onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })}
          />
          <Input
            placeholder="Contact phone (optional)"
            value={draft.contact_phone || ""}
            onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })}
          />

          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
            <Button variant="outline" onClick={cancelEdit} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="h-px bg-gray-200 my-6" />

      {/* Canvas connection section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Canvas connection</h2>
          <Button variant="outline" onClick={() => setShowToken((s) => !s)}>
            {showToken ? "Hide" : "Update token"}
          </Button>
        </div>

        {showToken && (
          <div className="space-y-2">
            {tokenOk && <div className="bg-green-50 text-green-700 text-sm p-2 rounded">{tokenOk}</div>}
            {tokenErr && <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{tokenErr}</div>}

            <div className="flex items-center gap-2">
              <Input
                placeholder="Paste new Canvas Admin Personal Access Token"
                value={token}
                onChange={(e) => {
                  setToken(e.target.value);
                  setTestResult(null);
                }}
                type={showTokenText ? "text" : "password"}
                className="flex-1"
              />
              <Button variant="secondary" onClick={() => setShowTokenText((s) => !s)} title={showTokenText ? "Hide" : "Show"}>
                {showTokenText ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button variant="secondary" onClick={pasteFromClipboard} title="Paste from clipboard">
                <ClipboardPaste className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={testConnection} disabled={testing || !org.canvas_base_url}>
                {testing ? "Testing…" : "Test connection"}
              </Button>
              {testResult?.user && (
                <div className="text-xs text-gray-700">
                  Signed in as{" "}
                  <span className="font-mono">
                    {testResult.user.name || testResult.user.login_id || testResult.user.id}
                  </span>
                  {testResult.admin_like_access ? " • admin endpoints OK" : " • limited permissions"}
                </div>
              )}
            </div>

            <Button onClick={saveToken} disabled={tokenSaving || !testResult?.user}>
              {tokenSaving ? "Saving…" : "Save token"}
            </Button>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-gray-200 my-6" />

      {/* Legal section */}
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">Legal</h2>
        <p className="text-sm text-gray-600">
          Review our{" "}
          <button type="button" className="text-blue-600 underline" onClick={() => setPolicyOpen(true)}>
            Privacy Policy
          </button>
          .
        </p>
      </div>

      <LiteModal open={policyOpen} onClose={() => setPolicyOpen(false)} title="Privacy Policy">
        <PrivacyPolicyBody />
      </LiteModal>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</div>
      <div className="border rounded-md px-3 py-2 bg-white">{value}</div>
    </div>
  );
}
